import { uuid } from "lib/uid";
import { get, isArray, isEmpty, isNil, last, mergeWith } from "lodash";
import { WHERE_OPERATORS } from "./useMainAccessors";

const deepMergeWithDeletions = (obj1, obj2) => {
  return mergeWith({}, obj1, obj2, (objValue, srcValue) => {
    if (isArray(srcValue)) {
      // Always replace arrays instead of merging
      return srcValue;
    }

    if (srcValue === null) {
      // Treat `null` in obj2 as an explicit deletion
      return undefined;
    }
  });
};

export const STORAGE_EVENT_KEY = "LOCAL_WILDSEA_DB";
export const LOCAL_MODE_USER_KEY = `${STORAGE_EVENT_KEY}_uid`;
const dispatchStorageEvent = (key) => {
  const event = new CustomEvent(STORAGE_EVENT_KEY, {
    detail: {
      key: key,
    },
  });
  window.dispatchEvent(event);
};
const storageGet = (key) => {
  return localStorage.getItem(key);
};
const storageSet = (key, value) => {
  localStorage.setItem(key, value);
  dispatchStorageEvent(key);
};
const storageRemove = (key) => {
  localStorage.removeItem(key);
};

//utils
const getLocalStorageKeysWithPrefix = (prefix) => {
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key.startsWith(prefix)) {
      keys.push(key);
    }
  }
  return keys;
};

//methods

const doc = (db, ...path) => {
  const strPath = path.join(",");

  return {
    type: "DOC",
    path,
    strPath,
    id: last(path),
    read: () => {
      try {
        return JSON.parse(storageGet(strPath));
      } catch (err) {
        return null;
      }
    },
    exists: !isNil(storageGet(path)),
  };
};
const collection = (db, ...path) => {
  const strPath = path.join(",");

  return {
    type: "COLLECTION",
    path,
    strPath,
    id: last(path),
  };
};
const getDoc = (doc) => {
  const val = doc.read();

  return {
    id: doc.id,
    data: () => val,
    exists: () => !isNil(val),
  };
};

const addDoc = (collection, data) => {
  const id = uuid();
  storageSet(`${collection.strPath},${id}`, JSON.stringify(data));
  return doc({}, ...collection.path, id);
};

const setDoc = (doc, data) => {
  storageSet(doc.strPath, JSON.stringify(deepMergeWithDeletions(doc.read(), data)));
};

const getDocs = (entity) => {
  switch (entity.type) {
    case "QUERY":
      return entity;
    default:
      const strPath = entity.strPath;
      const keys = getLocalStorageKeysWithPrefix(strPath);

      const docs = keys.map((k) => doc({}, ...k.split(",")));

      return { docs };
  }
};

const where = (key, operator, value) => {
  return {
    key,
    operator,
    value,
  };
};

const query = (collection, where) => {
  const { docs } = getDocs(collection);
  const filteredDocs = docs
    .filter((d) => {
      const data = get(d.read(), where.key);
      switch (where.operator) {
        case WHERE_OPERATORS.ARRAY_CONTAINS:
          return !!data.includes(where.value);
        default:
          throw new Error("operator not supported", JSON.stringify(where));
      }
    })
    .map((d) => getDoc(d));

  return {
    type: "QUERY",
    empty: isEmpty(filteredDocs),
    docs: filteredDocs,
  };
};

const runTransaction = (db, func) => {
  const transaction = {
    get: (doc) => getDoc(doc),
    update: (doc, data) => setDoc(doc, data),
  };

  func(transaction);
};

const updateDoc = (doc, data) => setDoc(doc, data);

const onSnapshot = (doc, nothing, callback) => {
  const key = doc.strPath;

  // Initial call with the current value of the key
  callback(getDoc(doc));

  // Listen for changes in the local storage
  const storageEventListener = (event) => {
    // Check if the changed key matches the one we're observing
    if (event.detail?.key === key) {
      // Trigger the callback with the new value
      callback(getDoc(doc));
    }
  };
  window.addEventListener(STORAGE_EVENT_KEY, storageEventListener);

  return () =>
    window.removeEventListener(STORAGE_EVENT_KEY, storageEventListener);
};

export const localModeMethods = {
  db: {},
  doc,
  collection,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  where,
  query,
  runTransaction,
  onSnapshot,
  deleteDoc: (doc) => storageRemove(doc.strPath),
};
